import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
    Dumbbell,
    TrendingUp,
    BarChart3,
    Target,
    Clock,
    Trophy,
    Users,
    Zap,
    Shield,
    ArrowRight,
    CheckCircle2,
    Star,
    Brain,
    Sparkles,
    Medal,
    Calculator
} from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import SEOHead from '../layout/SEOHead';
import DisplayAd from '../ads/DisplayAd';
import SidebarAd from '../ads/SidebarAd';
import ReviewService from '../../services/ReviewService';

const LandingPage = () => {
    const { t } = useTranslation('marketing');

    const [stats, setStats] = useState(null);

    useEffect(() => {
        ReviewService.getStats().then(data => {
            if (data && data.ratingCount > 0) {
                setStats(data);
            }
        });
    }, []);

    const seoTitle = t('landing.seo_title');
    const seoDescription = t('landing.hero_subtitle');

    const features = [
        {
            icon: <Brain className="text-red-500" size={32} />,
            title: t('landing.feature_ai'),
            description: t('landing.feature_ai_desc')
        },
        {
            icon: <Target className="text-red-500" size={32} />,
            title: t('landing.feature_planning'),
            description: t('landing.feature_planning_desc')
        },
        {
            icon: <BarChart3 className="text-red-500" size={32} />,
            title: t('landing.feature_analytics'),
            description: t('landing.feature_analytics_desc')
        },
        {
            icon: <Clock className="text-red-500" size={32} />,
            title: t('landing.feature_time'),
            description: t('landing.feature_time_desc')
        },
        {
            icon: <Shield className="text-red-500" size={32} />,
            title: t('landing.feature_social_safety'),
            description: t('landing.feature_social_safety_desc')
        },
        {
            icon: <Users className="text-red-500" size={32} />,
            title: t('landing.feature_trainer'),
            description: t('landing.feature_trainer_desc')
        },
        {
            icon: <Zap className="text-red-500" size={32} />,
            title: t('landing.feature_offline'),
            description: t('landing.feature_offline_desc')
        }
    ];

    const benefits = [
        t('landing.benefits.0'),
        t('landing.benefits.1'),
        t('landing.benefits.2'),
        t('landing.benefits.3'),
        t('landing.benefits.4'),
        t('landing.benefits.5'),
        t('landing.benefits.6'),
        t('landing.benefits.7'),
        t('landing.benefits.8'),
        t('landing.benefits.9'),
        t('landing.benefits.10'),
        t('landing.benefits.11')
    ];

    const websiteSchema = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "GymTracker Pro",
        "url": "https://gymtracker.pro",
        "potentialAction": {
            "@type": "SearchAction",
            "target": "https://gymtracker.pro/exercises?q={search_term_string}",
            "query-input": "required name=search_term_string"
        }
    };

    const softwareAppSchema = stats ? {
        "@context": "https://schema.org",
        "@type": ["SoftwareApplication", "MobileApplication"],
        "name": "GymTracker Pro",
        "alternateName": "GymTrackerPro",
        "applicationCategory": "HealthApplication",
        "applicationSubCategory": "Fitness",
        "operatingSystem": "Android, Web",
        "downloadUrl": "https://play.google.com/store/apps/details?id=gymtracker.pro",
        "installUrl": "https://play.google.com/store/apps/details?id=gymtracker.pro",
        "url": "https://gymtracker.pro",
        "image": "https://gymtracker.pro/pwa-512x512.png",
        "screenshot": "https://gymtracker.pro/og-image.png",
        "author": {
            "@type": "Person",
            "name": "Francisco Jesús Lara López",
            "url": "https://play.google.com/store/apps/developer?id=Francisco+Jes%C3%BAs+Lara+L%C3%B3pez"
        },
        "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": stats.ratingValue.toString(),
            "ratingCount": stats.ratingCount.toString(),
            "bestRating": "5",
            "worstRating": "1"
        },
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock"
        },
        "featureList": "Workout Tracking, 1RM Calculator, Macro Calculator, 3D Anatomy, AI Coach",
        "softwareVersion": "1.0.8",
        "contentRating": "Everyone",
        "inLanguage": ["es", "en", "fr", "de", "it", "pt", "pl"]
    } : null;

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [1, 2, 3, 4, 5, 6, 7].map(num => ({
            "@type": "Question",
            "name": t(`faq.q${num}`),
            "acceptedAnswer": {
                "@type": "Answer",
                "text": t(`faq.a${num}`)
            }
        }))
    };

    return (
        <div className="bg-black min-h-screen text-white flex flex-col">
            <SEOHead
                title={seoTitle}
                description={seoDescription}
                schema={[websiteSchema, faqSchema, softwareAppSchema].filter(Boolean)}
            />
            <Helmet>
                <meta name="keywords" content={t('landing.keywords')} />

                {/* Twitter */}
                <meta property="twitter:card" content="summary_large_image" />
                <meta property="twitter:url" content="https://gymtracker.pro/" />
                <meta property="twitter:title" content={`${t('landing.hero_title')} ${t('landing.hero_title_highlight')} - Gym Tracker Pro`} />
                <meta property="twitter:description" content={t('landing.hero_subtitle')} />
                <meta property="twitter:image" content="https://gymtracker.pro/og-image.jpg" />
            </Helmet>

            <PublicHeader />
            <main className="flex-grow">

                <SidebarAd side="left" />
                <SidebarAd side="right" />

                {/* Hero Section */}
                <section className="relative py-20 md:py-32 overflow-hidden">
                    {/* Background Effects */}
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(220,38,38,0.15),transparent_50%)]" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(220,38,38,0.1),transparent_50%)]" />

                    <div className="container mx-auto px-4 relative z-10">
                        <div className="max-w-4xl mx-auto text-center space-y-8">
                            {/* AI Badge */}
                            <div className="flex justify-center">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-red-900/40 to-black border border-red-500/30 text-red-400 text-sm font-bold animate-fade-in backdrop-blur-sm">
                                    <Sparkles size={16} className="text-yellow-500" />
                                    <span className="uppercase tracking-wider text-xs">
                                        {t('landing.ai_badge')}
                                    </span>
                                </div>
                            </div>

                            <h1 className="text-5xl md:text-7xl font-black uppercase italic tracking-wider leading-tight">
                                {t('landing.hero_title')} <span className="text-red-500">{t('landing.hero_title_highlight')}</span>
                                <br />
                                {t('landing.hero_subtitle_line')}
                            </h1>
                            <p className="text-xl md:text-2xl text-neutral-400 max-w-2xl mx-auto">
                                {t('landing.hero_subtitle')}
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mt-12">
                                <a
                                    href="/login"
                                    title="Comienza a registrar tus entrenamientos - Gym Tracker Pro"
                                    className="px-8 py-4 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-600 text-white font-black uppercase italic tracking-wider rounded-xl transition-all shadow-lg shadow-red-900/40 hover:shadow-red-900/60 border border-red-900/40 flex items-center gap-2"
                                >
                                    {t('landing.cta_start')}
                                    <ArrowRight size={20} />
                                </a>
                                <a
                                    href="/resources"
                                    className="px-8 py-4 bg-neutral-900 hover:bg-neutral-800 border border-neutral-700 hover:border-red-900/50 text-white font-black uppercase italic tracking-wider rounded-xl transition-all"
                                >
                                    {t('landing.cta_resources')}
                                </a>
                            </div>

                            {/* Real Visual Rating Proof or CTA */}
                            {stats ? (
                                <div className="flex flex-col items-center gap-2 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
                                    <div className="flex text-yellow-500">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star
                                                key={star}
                                                fill={star <= Math.round(stats.ratingValue) ? "currentColor" : "none"}
                                                className={star <= Math.round(stats.ratingValue) ? "text-yellow-500" : "text-neutral-700"}
                                                size={20}
                                            />
                                        ))}
                                    </div>
                                    <p className="text-neutral-400 text-sm font-medium">
                                        <span className="text-white font-bold">{stats.ratingValue}/5</span>
                                        {' ' + t('landing.rating_based_on', { count: stats.ratingCount })}
                                    </p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-1000 delay-300">
                                    <div className="flex text-neutral-700">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star key={star} size={20} className="text-neutral-700" />
                                        ))}
                                    </div>
                                    <p className="text-neutral-400 text-sm font-medium">
                                        {t('landing.be_first_to_rate')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {/* Free Tools Section */}
                <section className="py-24 bg-neutral-900/30 border-y border-neutral-800">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-3xl md:text-5xl font-black uppercase italic tracking-tight mb-6">
                                {t('landing.free_tools.title')} <span className="text-red-500">{t('landing.free_tools.title_highlight')}</span>
                            </h2>
                            <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
                                {t('landing.free_tools.subtitle')}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                            {/* Exercise Library Card */}
                            <div className="group relative bg-black border border-neutral-800 rounded-2xl p-8 hover:border-red-600/50 transition-all duration-300">
                                <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                                <div className="relative z-10 text-center">
                                    <div className="w-16 h-16 mx-auto bg-neutral-900 rounded-2xl flex items-center justify-center mb-6 border border-neutral-800 group-hover:bg-red-600 group-hover:text-white transition-colors">
                                        <Dumbbell size={32} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{t('landing.free_tools.wiki_title')}</h3>
                                    <p className="text-neutral-400 mb-6">
                                        {t('landing.free_tools.wiki_desc')}
                                    </p>
                                    <a
                                        href="/exercises"
                                        title={`${t('landing.free_tools.wiki_title')} - Gym Tracker Pro`}
                                        className="inline-flex items-center text-red-500 font-bold hover:text-red-400 transition-colors"
                                    >
                                        {t('landing.free_tools.wiki_cta')} <ArrowRight size={18} className="ml-2" />
                                    </a>
                                </div>
                            </div>

                            {/* 1RM Calculator Card */}
                            <div className="group relative bg-black border border-neutral-800 rounded-2xl p-8 hover:border-red-600/50 transition-all duration-300">
                                <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                                <div className="relative z-10 text-center">
                                    <div className="w-16 h-16 mx-auto bg-neutral-900 rounded-2xl flex items-center justify-center mb-6 border border-neutral-800 group-hover:bg-red-600 group-hover:text-white transition-colors">
                                        <TrendingUp size={32} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{t('landing.free_tools.onerm_title')}</h3>
                                    <p className="text-neutral-400 mb-6">
                                        {t('landing.free_tools.onerm_desc')}
                                    </p>
                                    <a
                                        href="/tools/1rm-calculator"
                                        title={`${t('landing.free_tools.onerm_title')} - Gym Tracker Pro`}
                                        className="inline-flex items-center text-red-500 font-bold hover:text-red-400 transition-colors"
                                    >
                                        {t('landing.free_tools.onerm_cta')} <ArrowRight size={18} className="ml-2" />
                                    </a>
                                </div>
                            </div>

                            {/* IPF Points Card */}
                            <div className="group relative bg-black border border-neutral-800 rounded-2xl p-8 hover:border-red-600/50 transition-all duration-300">
                                <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                                <div className="relative z-10 text-center">
                                    <div className="w-16 h-16 mx-auto bg-neutral-900 rounded-2xl flex items-center justify-center mb-6 border border-neutral-800 group-hover:bg-red-600 group-hover:text-white transition-colors">
                                        <Trophy size={32} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{t('landing.free_tools.ipf_title')}</h3>
                                    <p className="text-neutral-400 mb-6">
                                        {t('landing.free_tools.ipf_desc')}
                                    </p>
                                    <a
                                        href="/tools/ipf-calculator"
                                        title={`${t('landing.free_tools.ipf_title')} - Gym Tracker Pro`}
                                        className="inline-flex items-center text-red-500 font-bold hover:text-red-400 transition-colors"
                                    >
                                        {t('landing.free_tools.ipf_cta')} <ArrowRight size={18} className="ml-2" />
                                    </a>
                                </div>
                            </div>

                            {/* Plate Calculator Card */}
                            <div className="group relative bg-black border border-neutral-800 rounded-2xl p-8 hover:border-red-600/50 transition-all duration-300">
                                <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                                <div className="relative z-10 text-center">
                                    <div className="w-16 h-16 mx-auto bg-neutral-900 rounded-2xl flex items-center justify-center mb-6 border border-neutral-800 group-hover:bg-red-600 group-hover:text-white transition-colors">
                                        <Calculator size={32} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{t('landing.free_tools.plate_calc_title')}</h3>
                                    <p className="text-neutral-400 mb-6">
                                        {t('landing.free_tools.plate_calc_desc')}
                                    </p>
                                    <a
                                        href="/tools/plate-calculator"
                                        title={`${t('landing.free_tools.plate_calc_title')} - Gym Tracker Pro`}
                                        className="inline-flex items-center text-red-500 font-bold hover:text-red-400 transition-colors"
                                    >
                                        {t('landing.free_tools.plate_calc_cta')} <ArrowRight size={18} className="ml-2" />
                                    </a>
                                </div>
                            </div>

                            {/* Meet Simulator Card */}
                            <div className="group relative bg-black border border-neutral-800 rounded-2xl p-8 hover:border-red-600/50 transition-all duration-300 md:col-span-2 lg:col-span-2">
                                <div className="absolute inset-0 bg-gradient-to-br from-red-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl" />
                                <div className="relative z-10 text-center">
                                    <div className="w-16 h-16 mx-auto bg-neutral-900 rounded-2xl flex items-center justify-center mb-6 border border-neutral-800 group-hover:bg-red-600 group-hover:text-white transition-colors">
                                        <Medal size={32} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-2">{t('landing.free_tools.competition_hub_title')}</h3>
                                    <p className="text-neutral-400 mb-6 max-w-lg mx-auto">
                                        {t('landing.free_tools.competition_hub_desc')}
                                    </p>
                                    <a
                                        href="/tools/competition-hub"
                                        title={`${t('landing.free_tools.competition_hub_title')} - Gym Tracker Pro`}
                                        className="inline-flex items-center text-red-500 font-bold hover:text-red-400 transition-colors"
                                    >
                                        {t('landing.free_tools.competition_hub_cta')} <ArrowRight size={18} className="ml-2" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <DisplayAd className="mb-20" />

                {/* Features Grid */}
                <section className="py-20 bg-neutral-950/50">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-wider mb-4">
                                {t('landing.features_title')} <span className="text-red-500">{t('landing.features_title_highlight')}</span>
                            </h2>
                            <p className="text-xl text-neutral-400 max-w-2xl mx-auto">
                                {t('landing.features_subtitle')}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {features.map((feature, idx) => (
                                <div
                                    key={idx}
                                    className="bg-neutral-900 border border-neutral-800 hover:border-red-900/50 rounded-xl p-6 transition-all hover:shadow-lg hover:shadow-red-900/10 group"
                                >
                                    <div className="p-3 bg-red-900/10 border border-red-900/30 rounded-lg inline-block mb-4 group-hover:bg-red-900/20 transition-colors">
                                        {feature.icon}
                                    </div>
                                    <h3 className="text-xl font-black uppercase tracking-wider mb-3">
                                        {feature.title}
                                    </h3>
                                    <p className="text-neutral-400 leading-relaxed">
                                        {feature.description}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-20">
                    <div className="container mx-auto px-4">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-wider mb-4">
                                    {t('landing.benefits_title')} <span className="text-red-500">{t('landing.benefits_title_highlight')}</span>?
                                </h2>
                                <p className="text-xl text-neutral-400">
                                    {t('landing.benefits_subtitle')}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {benefits.map((benefit, idx) => (
                                    <div
                                        key={idx}
                                        className="flex items-start gap-3 bg-neutral-900/50 border border-neutral-800 rounded-lg p-4 hover:border-red-900/30 transition-colors"
                                    >
                                        <CheckCircle2 className="text-red-500 flex-shrink-0 mt-1" size={20} />
                                        <span className="text-neutral-300">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
                <DisplayAd className="mt-12" />

                {/* FAQ Section */}
                <section className="py-20 bg-neutral-900/30">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto">
                            <div className="text-center mb-12">
                                <h2 className="text-3xl md:text-4xl font-black uppercase italic tracking-wider mb-4">
                                    {t('faq.title')}
                                </h2>
                                <p className="text-lg text-neutral-400">
                                    {t('faq.subtitle')}
                                </p>
                            </div>

                            <div className="space-y-6">
                                {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                                    <div key={num} className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 hover:border-red-900/30 transition-colors">
                                        <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-3">
                                            <span className="text-red-500 font-black">Q.</span>
                                            {t(`faq.q${num}`)}
                                        </h3>
                                        <p className="text-neutral-400 pl-8 leading-relaxed">
                                            {t(`faq.a${num}`)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-20 bg-gradient-to-b from-neutral-950 to-black">
                    <div className="container mx-auto px-4">
                        <div className="max-w-3xl mx-auto text-center space-y-8">
                            <h2 className="text-4xl md:text-5xl font-black uppercase italic tracking-wider">
                                {t('landing.cta_final_title')} <span className="text-red-500">{t('landing.cta_final_highlight')}</span>?
                            </h2>
                            <p className="text-xl text-neutral-400">
                                {t('landing.cta_final_subtitle')}
                            </p>
                            <a
                                href="/login"
                                title="Empezar ahora - Gym Tracker Pro"
                                className="inline-flex items-center gap-2 px-10 py-5 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-600 text-white font-black uppercase italic tracking-wider rounded-xl transition-all shadow-lg shadow-red-900/40 hover:shadow-red-900/60 border border-red-900/40 text-lg"
                            >
                                {t('landing.cta_now')}
                                <ArrowRight size={24} />
                            </a>
                            <p className="text-sm text-neutral-500">
                                {t('landing.free_notice')}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Project Status Banner */}
                <section className="py-12 bg-black border-t border-neutral-900">
                    <div className="container mx-auto px-4 text-center">
                        <div className="max-w-3xl mx-auto bg-neutral-900/40 rounded-xl p-8 border border-neutral-800/50 backdrop-blur-sm">
                            <div className="flex justify-center mb-4">
                                <Zap className="text-yellow-500 animate-pulse" size={24} />
                            </div>
                            <h3 className="text-white font-bold uppercase tracking-wider mb-3 text-sm">
                                {t('project_status.title')}
                            </h3>
                            <p className="text-neutral-400 text-sm leading-relaxed max-w-2xl mx-auto">
                                {t('project_status.text')}
                            </p>
                        </div>
                    </div>
                </section>

            </main>
            <PublicFooter />
        </div>
    );
};

export default LandingPage;
